import { Box, Button, Grid, InputAdornment, MenuItem } from '@material-ui/core';
import { BorderBottom, BorderBottomRounded, Event } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import {
  changeMail,
  changeMailOnBackend,
  changePhoneOnBackend,
  confirmChangeEmailCode,
  getAddress,
  getInfosIndividual,
  updateStudentCoordonnes,
} from '@webapps/business';
import {
  FormikDatePicker,
  FormikTextField,
  Modal,
  TextField,
  TextFieldPx,
  useAuth,
  useMutation,
  useQuery,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';

import theme from './theme';

const {
  colors: { background, subTextColor },
} = theme;

const PersonalInfos = () => {
  const [addresses, setAddresses] = useState([]);
  const [originalEmail, setOriginalEmail] = useState('');
  const [email, setEmail] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  const [phone, setPhone] = useState('');
  const [errorPhone, setErrorPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [phoneClasses, setPhoneClasses] = useState('phoneInput');
  const phoneClassesRef = useRef();
  phoneClassesRef.current = phoneClasses;
  const [addressSearchTimeout, setAddressSearchTimeout] = useState(null);
  const [saveTimeout, setSaveTimeout] = useState(null);

  const {
    error: errorRequest,
    isLoading: isLoadingUpdateMail,
    isSuccess,
    mutate: mutateChangeMail,
  } = useMutation(changeMail);

  const {
    error: errorRequestConfirmCode,
    isLoading: isLoadingUpdateConfirmCode,
    isSuccess: isSuccessConfirmCode,
    mutate: mutateConfirmCodeMail,
  } = useMutation(confirmChangeEmailCode);

  const {
    error: errorRequestChangeMailBackend,
    isLoading: isLoadingChangeMailBackend,
    isSuccess: isSuccessChangeMailBackend,
    mutate: mutateChangeMailBackend,
  } = useMutation(changeMailOnBackend);

  const {
    error: errorRequestChangePhoneBackend,
    isLoading: isLoadingChangePhoneBackend,
    isSuccess: isSuccessChangePhoneBackend,
    mutate: mutateChangePhoneBackend,
  } = useMutation(changePhoneOnBackend);

  const handleChangeEmail = async () => {
    await mutateChangeMail({
      email,
    });
  };

  const handleConfirmCode = async () => {
    await mutateConfirmCodeMail({
      code,
    });
  };

  const handleChangePhone = (phoneInfo) => {
    if (phoneInfo) {
      setPhone(phoneInfo);
      if (isValidPhoneNumber(phoneInfo)) {
        setErrorPhone(false);
        setPhoneClasses('phoneInput');
      } else {
        setErrorPhone(true);
        setPhoneClasses('phoneInputError');
      }
    }
  };

  const {
    classes,
    css,
    cx,
    global,
    theme: { spacing },
  } = useStyles(styles);

  const { idUser } = useAuth();

  const {
    data: infos = {},
    isLoadingProfilInfo,
    refetch,
  } = useQuery(getInfosIndividual, {
    key: ['profilInfoID', idUser],
    variables: { id: idUser },
  });

  const handleCloseUpdateCoor = () => {
    formik.handleSubmit();
  };

  const {
    error: errorRequestUpdate,
    isLoading: isLoadingUpdate,
    isSuccess: isSuccessUpdate,
    mutateAsync: mutateUpdateCoordonnes,
  } = useMutation(updateStudentCoordonnes);

  const { mutate: getAllInfoAdresses, mutateAsync: getAllInfoAdressesAsync } = useMutation(getAddress, {
    onSuccess: (data) => {
      setAddresses(data?.data?.features || []);
    },
  });

  const { addToast } = useToast();

  useEffect(() => {
    if (isSuccessUpdate) {
      addToast({
        status: 'success',
        message: 'Opération réussie',
      });
      close();
      refetch();
    }
    if (errorRequestUpdate) {
      addToast({
        status: 'error',
        message: 'Une erreur est survenue',
      });
    }
  }, [isSuccessUpdate, errorRequestUpdate]);

  const formik = useFormik({
    initialValues: {
      firstname: infos?.firstname || '',
      lastname: infos?.lastname || '',
      phone: infos?.phone || '',
      email: infos?.email || '',
      birthDate: infos?.birthDate || '',
      gender: infos?.gender || '',
      address: {
        label: infos?.address?.label || '',
        city: infos?.address?.city || '',
        cityCode: infos?.address?.cityCode || '',
      },
    },
    enableReinitialize: true,
    onSubmit: async ({ birthDate, ...values }) => {
      await mutateUpdateCoordonnes({
        data: {
          birthDate: birthDate ? format(new Date(birthDate), "yyyy-MM-dd'T'HH:mm:ss" ) : null,
          ...values,
        },
        id: idUser,
      });
    },
  });

  const getAdresses = useMutation(getAddress, {
    onSuccess: (data) => {
      setAddresses(data?.data?.features);
    },
  });

  const handleChangeAddress = async (searchValue) => {
    if (!searchValue.target.value || searchValue.target.value.length < 3) {
      return null;
    }
    
    if (addressSearchTimeout) {
      clearTimeout(addressSearchTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      getAdresses.mutateAsync({ name: searchValue.target.value });
    }, 300);
    
    setAddressSearchTimeout(newTimeout);
    
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    const newSaveTimeout = setTimeout(() => {
      handleCloseUpdateCoor();
    }, 500);
    
    setSaveTimeout(newSaveTimeout);
  };

  const changeAddress = async (value) => {
    if (!value || value.length < 3) {
      return null;
    }
    
    if (addressSearchTimeout) {
      clearTimeout(addressSearchTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      getAllInfoAdresses({ name: value || 'test' });
    }, 300);
    
    setAddressSearchTimeout(newTimeout);
  };

  useEffect(() => {
    if (infos?.email) {
      setOriginalEmail(infos?.email);
      setEmail(infos?.email);
    }
    if (infos?.phone) {
      setOriginalPhone(infos?.phone);
      setPhone(infos?.phone);
    }
    
    if (infos && Object.keys(infos).length > 0) {
      formik.setValues({
        firstname: infos?.firstname || '',
        lastname: infos?.lastname || '',
        email: infos?.email || '',
        phone: infos?.phone || '',
        birthDate: infos?.birthDate
          ? format(new Date(infos?.birthDate), 'yyyy-MM-dd')
          : '',
        gender: infos?.gender || '',
        address: {
          label: infos?.address?.label || '',
          city: infos?.address?.city || '',
          cityCode: infos?.address?.cityCode || '',
        },
      });
    }
  }, [infos]);

  useEffect(() => {
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Vous avez reçu un code',
      });
      setCodeSent(true);
    }
  }, [isSuccess]);

  useEffect(async () => {
    if (isSuccessConfirmCode) {
      await mutateChangeMailBackend({
        email: email,
        id: idUser,
      });
    }
  }, [isSuccessConfirmCode]);

  useEffect(async () => {
    if (isSuccessChangeMailBackend) {
      addToast({
        status: 'success',
        message: 'Votre e-mail a été mis à jour',
      });
      refetch();
      setCodeSent(false);
    }
    if (isSuccessChangePhoneBackend) {
      addToast({
        status: 'success',
        message: 'Votre numéro de téléphone a été mis à jour',
      });
      refetch();
      setPhoneInput(false);
    }
  }, [isSuccessChangeMailBackend, isSuccessChangePhoneBackend]);

  useEffect(() => {
    if (errorRequestConfirmCode) {
      addToast({
        status: 'error',
        message: 'Le code est incorrect',
      });
    }
    if (errorRequestChangeMailBackend || errorRequestChangePhoneBackend) {
      addToast({
        status: 'error',
        message: 'Une erreur est survenue',
      });
    }
  }, [
    errorRequestConfirmCode,
    errorRequestChangeMailBackend,
    errorRequestChangePhoneBackend,
  ]);

  return (
    <div className={classes.container}>
      <div className={global.title}>
        <div>Coordonnées</div>
      </div>
      <div style={{ marginTop: '8px' }} className={global.subTitle}>
        <FormattedMessage id="student.profil.personnalinfos.subTitle" />
      </div>
      <Grid
        container
        spacing={2}
        className={css({
          marginTop: 20,
          '& > .MuiGrid-item': { padding: '10px' },
        })}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          xl={6}
          style={{ width: '100%' }}
        >
          <PhoneInput
            className={classes[phoneClassesRef.current]}
            countryCallingCodeEditable={false}
            defaultCountry="FR"
            international
            limitMaxLength
            name="phone"
            onChange={(phone) => {
              handleChangePhone(phone);
            }}
            placeholder={<FormattedMessage id="createUserForm.phone" />}
            value={phone}

          />
        </Grid>
        <Grid
          className={classes.personalEmail}
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          xl={6}
          style={{
            width: '100%',
          }}
        >
          <FormikTextField
            formik={formik}
            fullWidth
            borderRadius={6}
            label={<FormattedMessage id="E-mail" />}
            name="email"
            inputProps={{
              className: cx(classes.infoBox, global.infoText),
            }}
            variant="filled"
          />
        </Grid>
        <Grid
          item
          xs={12}
          className={classes.personalAddress}
          style={{ width: '100%', padding: '10px' }}
        >
          <Grid item xs className={classes.personalAddressAC}>
            <Autocomplete
              fullWidth
              autoComplete={false}
              classes={{
                inputRoot: css({
                  height: '45px!important',
                }),
              }}
              value={formik.values.address.label}
              getOptionLabel={(option) => option || ''}
              onInputChange={(event, value) => {
                if (value && value.length >= 3) {
                  changeAddress(value);
                }
              }}
              onChange={(_, newValue) => {
                if (newValue) {
                  const selectedAddress = addresses.find(addr => addr?.properties?.label === newValue);
                  if (selectedAddress) {
                    formik.setFieldValue('address.label', selectedAddress?.properties?.label);
                    formik.setFieldValue('address.city', selectedAddress?.properties?.city);
                    formik.setFieldValue('address.cityCode', selectedAddress?.properties?.postcode);
                    
                    if (saveTimeout) {
                      clearTimeout(saveTimeout);
                    }
                    
                    const newSaveTimeout = setTimeout(() => {
                      handleCloseUpdateCoor();
                    }, 500);
                    
                    setSaveTimeout(newSaveTimeout);
                  }
                }
              }}
              options={addresses?.map((option) => option?.properties?.label) || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  borderRadius={6}
                  label={<FormattedMessage id="Adresse" />}
                  name="address.label"
                  onChange={(value) => {
                    if (value.target.value && value.target.value.length >= 3) {
                      handleChangeAddress(value);
                    }
                  }}
                  variant="outlined"
                  size="small"
                />
              )}
              freeSolo={false}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          style={{ padding: '10px' }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={6}
            xl={6}
            className={classes.personalAddress}
          >
            <FormikTextField
              formik={formik}
              fullWidth
              borderRadius={6}
              label={<FormattedMessage id="Code Postal" />}
              name="address.cityCode"
              inputProps={{
                className: cx(
                  classes.infoBox,
                  global.infoText,
                ),
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={6}
            xl={6}
            className={classes.personalAddress}
          >
            <FormikTextField
              formik={formik}
              fullWidth
              borderRadius={6}
              label={<FormattedMessage id="Ville" />}
              name="address.city"
              inputProps={{
                className: cx(
                  classes.infoBox,
                  global.infoText,
                ),
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
        </Grid>
        {phone && phone !== originalPhone && (
          <Grid item xs={12} className={classes.textfield}>
            <Button
              disabled={errorPhone || isLoadingChangePhoneBackend}
              color="primary"
              variant="contained"
              onClick={async () => {
                await mutateChangePhoneBackend({ phone: phone, id: idUser });
              }}
            >
              Enregistrer
            </Button>
          </Grid>
        )}
        {email && email !== originalEmail && (
          <Grid item xs={12} className={classes.textfield}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleChangeEmail}
            >
              Envoyer le code
            </Button>
          </Grid>
        )}
      </Grid>
      <Modal
        onCancel={() => setCodeSent(false)}
        open={codeSent}
        title="Veuillez confirmer votre code"
      >
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <div>
              Veuillez vérifier votre e-mail : {email}, pour le code de
              confirmation.
            </div>
          </Grid>
          <Grid item xs={12}>
            <TextFieldPx
              fullWidth
              borderRadius={6}
              label={<FormattedMessage id="code" />}
              name="code"
              type="tel"
              onChange={(e) => setCode(e.target.value)}
              value={code}
            />
          </Grid>
          <Grid className={css({ marginTop: spacing(2) })} xs={6}>
            <Button
              className={css({ borderRadius: '400px' })}
              color="primary"
              disabled={
                isLoadingUpdateConfirmCode || isLoadingChangeMailBackend
              }
              fullWidth
              onClick={handleConfirmCode}
              variant="contained"
            >
              <FormattedMessage id="btnConfirm" />
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
};

const styles = ({ spacing }) => ({
  container: {
    background,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    maxWidth: '100%',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    marginTop: spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textarea: {
    marginTop: spacing(2),
    resize: 'none',
    fontFamily: 'Roboto',
    color: 'black',
    '@media only screen and (max-width:1920px)': { fontSize: '13.500px' },
    '@media only screen and (max-width:1600px)': { fontSize: '13.5000px' },
    backgroundColor: '#f7f9fa',
    borderRadius: 20,
    borderColor: 'white',
    padding: spacing(0, 2),
  },
  modifyLink: {
    a: {
      textDecoration: 'none',
      color: '#0183FF',
    },
    padding: spacing(0),
    wordWrap: 'break-word',
    maxWidth: '100%',
  },
  label: {
    fontSize: 13,
  },
  infoBox: {
    borderRadius: '5px',
    color: '#152652',
    height: '30px',
    backgroundColor: '#F3F7FF',
  },
  personalAddress: {
    '@media (max-width:768px)': { padding: spacing(0, 0, 2.5, 0) },
  },
  personalAddressAC: {
    '@media (max-width:768px)': { padding: spacing(0, 0, 2.5, 0) },
  },
  textfield: {
    marginTop: spacing(2.5),
    flexBasis: 'auto',
  },
  btnCancel: {
    marginLeft: spacing(2),
    backgroundColor: 'crimson',
  },
  phoneInput: {
    height: '100%',
    minHeight: "45px",
    '& > *': {
      backgroundColor: '#F3F7FF',
      border: '1px solid #C0D9FF',
      borderRadius: 5,
      padding: spacing(0, 2),
      height: '100%',
      color: '#737373',
      outline: 'none',
    },
  },
  phoneInputError: {
    height: '100%',
    '& > *': {
      backgroundColor: '#F3F7FF',
      border: '1px solid red',
      borderRadius: 5,
      padding: spacing(0, 2),
      height: '100%',
      color: '#f44336',
      outline: 'none',
    },
  },
  phoneError: {
    display: 'flex',
    width: '100%',
    '& > *': {
      width: '50%',
    },
  },
  phoneDisabled: {
    '& > *': {
      backgroundColor: 'white',
    },
  },
  personalEmail: {
    '@media (max-width: 1200px)': {
      marginTop: spacing(0) + ' !important',
    },
  },
});

export default PersonalInfos;
